import { Heading, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import moment from "moment/min/moment-with-locales";
import { Link } from "react-router";
import { eventType } from "../../hardcoded/events";
import { getPrefered } from "../../utils/preferedLanguage";
import { HighlightedBlock } from "./HighlightedBlock";

type EventButtonProps = {
    event: eventType;
    details?: boolean;
    width?: string;
};

const formatDate = (m: moment.Moment) => m.format("D. M. YYYY");
const formatTime = (m: moment.Moment) => m.format("H:mm");

/** Formats the displayed date(range) of an event (moments are already in the user's timezone). */
function formatEventDate(from: moment.Moment, to: moment.Moment, isFullDay?: boolean): string {
    if (isFullDay) {
        return from.isSame(to, "day") ? formatDate(from) : `${formatDate(from)} - ${formatDate(to)}`;
    }
    if (from.isSame(to)) {
        return `${formatDate(from)} ${formatTime(from)}`;
    }
    if (from.isSame(to, "day")) {
        return `${formatDate(from)} ${formatTime(from)} - ${formatTime(to)}`;
    }
    return `${formatDate(from)} ${formatTime(from)} - ${formatDate(to)} ${formatTime(to)}`;
}

/** The full ISO 8601 datetime (range) shown on hover. */
function formatIsoRange(from: moment.Moment, to: moment.Moment): string {
    return from.isSame(to) ? from.toISOString(true) : `${from.toISOString(true)} - ${to.toISOString(true)}`;
}

export const EventButton = ({
    event: { link, title, from, to, location, description, isFullDay },
    details,
}: EventButtonProps) => {
    const isInternal = link.startsWith("/") || link.startsWith("#");

    const content = (
        <Stack direction="column">
            <Text title={formatIsoRange(from, to)}>{formatEventDate(from, to, isFullDay)}</Text>
            <Heading fontSize="1xl" fontWeight={"semibold"} color={useColorModeValue("brand.500", "brand.400")}>
                {getPrefered(title)}
            </Heading>
            <Text fontStyle="italic">{location}</Text>
            {details && <Text>{getPrefered(description)}</Text>}
        </Stack>
    );

    return (
        <HighlightedBlock>
            {link ? (
                <Link to={link} target={isInternal ? "_self" : "_blank"} rel="noopener noreferrer">
                    {content}
                </Link>
            ) : (
                content
            )}
        </HighlightedBlock>
    );
};
